import { useState, useMemo } from 'react'
import { Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import { Search, Newspaper, Calendar, User, ArrowRight, Sparkles, Filter } from 'lucide-react'
import { useCollection } from '../../hooks/useFirestore'
import { formatDate, truncate } from '../../utils/helpers'

const blogCategories = [
  { value: 'all', label: 'All Posts' },
  { value: 'devotional', label: 'Devotionals' },
  { value: 'sermon-recap', label: 'Sermon Recaps' },
  { value: 'church-news', label: 'Church News' },
  { value: 'testimony', label: 'Testimonies' },
  { value: 'teaching', label: 'Teaching' },
]

const categoryLabel = (val) =>
  blogCategories.find((c) => c.value === val)?.label || val

const Blog = () => {
  const [search, setSearch] = useState('')
  const [category, setCategory] = useState('all')

  const { data: posts, loading } = useCollection('blogPosts', {
    orderByField: 'publishedAt',
    orderDirection: 'desc',
  })

  const filtered = useMemo(() => {
    const published = (posts || []).filter((p) => p.isPublished)
    return published.filter((p) => {
      const matchesCategory = category === 'all' || p.category === category
      const matchesSearch = !search.trim()
        || p.title?.toLowerCase().includes(search.toLowerCase())
        || p.excerpt?.toLowerCase().includes(search.toLowerCase())
        || p.tags?.some((t) => t.toLowerCase().includes(search.toLowerCase()))
      return matchesCategory && matchesSearch
    })
  }, [posts, search, category])

  return (
    <>
      {/* Hero */}
      <section className="relative py-24 md:py-32 overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-br from-dark-100 to-dark" />
          <div className="absolute top-1/3 left-1/4 w-[500px] h-[500px] bg-primary-500/20 rounded-full blur-3xl" />
          <div className="absolute bottom-1/3 right-1/4 w-[500px] h-[500px] bg-secondary-500/20 rounded-full blur-3xl" />
        </div>

        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <motion.span
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 backdrop-blur-sm text-white text-sm font-semibold mb-6 border border-white/20"
              whileHover={{ scale: 1.05 }}
            >
              <Newspaper className="w-4 h-4 text-accent-400" />
              From Our Church
            </motion.span>
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-heading font-bold text-white mb-6">
              The <span className="bg-gradient-to-r from-accent-400 to-orange-400 bg-clip-text text-transparent">Blog</span>
            </h1>
            <p className="text-xl text-white/70 max-w-2xl mx-auto">
              Devotionals, sermon recaps, church news, and stories from the DLBC
              Clarksville family.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Filters */}
      <section className="py-10 bg-light-100 border-b border-light-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-stretch md:items-center gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search posts..."
                className="w-full pl-12 pr-4 py-3.5 border border-gray-200 rounded-2xl bg-white focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>
            <div className="flex items-center gap-2 overflow-x-auto pb-1">
              <Filter className="w-4 h-4 text-gray-500 flex-shrink-0" />
              {blogCategories.map((cat) => (
                <button
                  key={cat.value}
                  onClick={() => setCategory(cat.value)}
                  className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${
                    category === cat.value
                      ? 'bg-primary-600 text-white'
                      : 'bg-white text-dark-300 hover:bg-primary-50'
                  }`}
                >
                  {cat.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Posts */}
      <section className="py-16 md:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="text-center py-16">
              <div className="animate-spin w-10 h-10 border-2 border-primary-600 border-t-transparent rounded-full mx-auto" />
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-16">
              <Newspaper className="w-14 h-14 mx-auto mb-4 text-gray-300" />
              <h3 className="text-xl font-heading font-bold text-dark-100 mb-2">
                {posts?.length ? 'No posts match your search' : 'No blog posts yet'}
              </h3>
              <p className="text-dark-300">
                {posts?.length
                  ? 'Try a different category or search term.'
                  : 'Check back soon — we\'re writing.'}
              </p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filtered.map((post, idx) => (
                <motion.article
                  key={post.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: idx * 0.05 }}
                  className="group"
                >
                  <Link to={`/blog/${post.slug}`} className="block">
                    <div className="bg-white rounded-3xl overflow-hidden shadow-soft border border-light-200 hover:shadow-xl transition-all duration-300 h-full flex flex-col">
                      <div className="relative h-52 overflow-hidden bg-gradient-to-br from-primary-100 to-secondary-100">
                        {post.coverImageUrl ? (
                          <img
                            src={post.coverImageUrl}
                            alt={post.title}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <Newspaper className="w-14 h-14 text-primary-300" />
                          </div>
                        )}
                        {post.category && (
                          <span className="absolute top-4 left-4 px-3 py-1 rounded-full bg-white/95 backdrop-blur-sm text-xs font-bold text-primary-700 shadow-md">
                            {categoryLabel(post.category)}
                          </span>
                        )}
                      </div>
                      <div className="p-6 flex flex-col flex-1">
                        <h3 className="text-xl font-heading font-bold text-dark-100 mb-3 group-hover:text-primary-600 transition-colors">
                          {post.title}
                        </h3>
                        <p className="text-dark-300 text-sm mb-4 flex-1">
                          {truncate(post.excerpt, 140)}
                        </p>
                        <div className="flex items-center justify-between pt-4 border-t border-light-200 text-sm text-dark-300">
                          <span className="flex items-center gap-1.5">
                            <User className="w-4 h-4" />
                            {post.author}
                          </span>
                          <span className="flex items-center gap-1.5">
                            <Calendar className="w-4 h-4" />
                            {post.publishedAt
                              ? formatDate(post.publishedAt?.toDate?.() || post.publishedAt, 'MMM d, yyyy')
                              : ''}
                          </span>
                        </div>
                        <div className="mt-4 inline-flex items-center gap-2 text-primary-600 font-semibold text-sm group-hover:gap-3 transition-all">
                          Read More
                          <ArrowRight className="w-4 h-4" />
                        </div>
                      </div>
                    </div>
                  </Link>
                </motion.article>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Newsletter CTA */}
      <section className="py-20 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-primary-500 via-secondary-500 to-accent-500" />
        <div className="relative max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Sparkles className="w-12 h-12 text-white mx-auto mb-4" />
          <h2 className="text-3xl md:text-4xl font-heading font-bold text-white mb-4">
            Don't miss a post
          </h2>
          <p className="text-lg text-white/80 mb-2 max-w-2xl mx-auto">
            Subscribe to our newsletter — links in the footer below.
          </p>
        </div>
      </section>
    </>
  )
}

export default Blog
